/**
 * FeedPage - Instagram-style feed for haircut posts
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, limit, getDocs, doc, updateDoc, increment, addDoc, deleteDoc, Timestamp, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { Post, PostComment } from '@/types';
import { Heart, MessageCircle, Send, MoreVertical, Plus, Edit2, Trash2, Settings, X, AlertTriangle, Flag } from 'lucide-react';
import { ReportContentModal } from '@/components/modals/ReportContentModal';
import { toast } from 'sonner';
import { getFranchiseIdFromUrl } from '@/config/franchiseUrls';

export default function FeedPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, firebaseUser, customClaims } = useAuth();
  const permissions = usePostPermissions();

  // Check if user is a guest - guests can view but not interact
  const isGuest = customClaims?.role === 'guest' || user?.role === 'guest' || firebaseUser?.isAnonymous;
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState<Record<string, string>>({});
  const [showComments, setShowComments] = useState<Record<string, boolean>>({});
  const [comments, setComments] = useState<Record<string, PostComment[]>>({});
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [showPostMenu, setShowPostMenu] = useState<string | null>(null);
  const [deletingPost, setDeletingPost] = useState<string | null>(null);
  const [deleteConfirmPost, setDeleteConfirmPost] = useState<Post | null>(null);
  const [reportPost, setReportPost] = useState<Post | null>(null);
  const [reportComment, setReportComment] = useState<{ comment: PostComment; postId: string; franchiseId?: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load posts
  useEffect(() => {
    loadPosts();
  }, [firebaseUser]);

  // Load liked posts after posts are loaded
  useEffect(() => {
    if (posts.length > 0 && firebaseUser) {
      loadLikedPosts();
    }
  }, [posts.length, firebaseUser]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowPostMenu(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPosts = async () => {
    try {
      // CRITICAL: Get franchiseId from URL to filter posts
      const franchiseId = user?.franchiseId || getFranchiseIdFromUrl();

      if (!franchiseId) {
        console.error('❌ No franchiseId available for feed');
        toast.error(t('feed.couldNotDetermineFranchise'));
        setLoading(false);
        return;
      }

      console.log('✅ Loading posts for franchiseId:', franchiseId);

      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('franchiseId', '==', franchiseId),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const postsData = snapshot.docs.map(doc => ({
        postId: doc.id,
        ...doc.data()
      })) as Post[];

      console.log(`✅ Loaded ${postsData.length} posts for franchise ${franchiseId}`);
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error(t('feed.feedLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadLikedPosts = async () => {
    if (!firebaseUser) return;

    try {
      const liked = new Set<string>();

      for (const post of posts) {
        const likesRef = collection(db, 'posts', post.postId, 'likes');
        const q = query(likesRef, where('userId', '==', firebaseUser.uid));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          liked.add(post.postId);
        }
      }

      setLikedPosts(liked);
    } catch (error) {
      console.error('Error loading liked posts:', error);
    }
  };

  const loadComments = async (postId: string) => {
    try {
      const commentsRef = collection(db, 'posts', postId, 'comments');
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);

      const commentsData = snapshot.docs.map(doc => ({
        commentId: doc.id,
        ...doc.data()
      })) as PostComment[];

      setComments(prev => ({ ...prev, [postId]: commentsData }));
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleLike = async (postId: string) => {
    if (!firebaseUser || !user) return;

    try {
      const likesRef = collection(db, 'posts', postId, 'likes');

      // ALWAYS check Firestore for existing like (don't trust local state)
      const q = query(likesRef, where('userId', '==', firebaseUser.uid));
      const snapshot = await getDocs(q);
      const existingLike = !snapshot.empty;

      if (existingLike) {
        // Unlike - delete existing like
        await deleteDoc(doc(db, 'posts', postId, 'likes', snapshot.docs[0].id));

        // Decrement count
        await updateDoc(doc(db, 'posts', postId), {
          likesCount: increment(-1)
        });

        setLikedPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });

        setPosts(prev => prev.map(p =>
          p.postId === postId ? { ...p, likesCount: p.likesCount - 1 } : p
        ));
      } else {
        // Like - create new like
        await addDoc(likesRef, {
          postId,
          userId: firebaseUser.uid,
          userName: user.displayName || 'Usuario',
          userPhoto: user.photoURL,
          createdAt: Timestamp.now()
        });

        // Increment count
        await updateDoc(doc(db, 'posts', postId), {
          likesCount: increment(1)
        });

        setLikedPosts(prev => new Set(prev).add(postId));

        setPosts(prev => prev.map(p =>
          p.postId === postId ? { ...p, likesCount: p.likesCount + 1 } : p
        ));
      }
    } catch (error) {
      console.error('Error liking post:', error);
      toast.error(t('feed.likeError'));
    }
  };

  const handleComment = async (postId: string) => {
    if (!firebaseUser || !user || !commentText[postId]?.trim()) return;

    try {
      const commentsRef = collection(db, 'posts', postId, 'comments');

      await addDoc(commentsRef, {
        postId,
        userId: firebaseUser.uid,
        userName: user.displayName || 'Usuario',
        userPhoto: user.photoURL,
        userRole: user.role,
        text: commentText[postId].trim(),
        createdAt: Timestamp.now()
      });

      // Increment count
      await updateDoc(doc(db, 'posts', postId), {
        commentsCount: increment(1)
      });

      setPosts(prev => prev.map(p =>
        p.postId === postId ? { ...p, commentsCount: p.commentsCount + 1 } : p
      ));

      setCommentText(prev => ({ ...prev, [postId]: '' }));

      // Reload comments
      await loadComments(postId);

      toast.success(t('feed.commentPublished'));
    } catch (error) {
      console.error('Error commenting:', error);
      toast.error(t('feed.commentError'));
    }
  };

  const toggleComments = async (postId: string) => {
    const isShowing = showComments[postId];

    if (!isShowing && !comments[postId]) {
      await loadComments(postId);
    }

    setShowComments(prev => ({ ...prev, [postId]: !isShowing }));
  };

  const formatTimeAgo = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.now');
    if (diffMins < 60) return t('notifications.minutesAgo', { minutes: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { hours: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { days: diffDays });

    return date.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' });
  };

  // Check if current user can edit a specific post
  const canEditPost = (post: Post): boolean => {
    if (!firebaseUser || permissions.loading) return false;

    // Author can always edit their own post (if they have canEdit permission)
    if (post.authorId === firebaseUser.uid && permissions.canEdit) return true;

    // Users with canEditAny permission can edit any post
    if (permissions.canEditAny) return true;

    return false;
  };

  // Check if current user can delete a specific post
  const canDeletePost = (post: Post): boolean => {
    if (!firebaseUser || permissions.loading) return false;

    // Author can always delete their own post (if they have canDelete permission)
    if (post.authorId === firebaseUser.uid && permissions.canDelete) return true;

    // Users with canDeleteAny permission can delete any post
    if (permissions.canDeleteAny) return true;

    return false;
  };

  const handleEditPost = (postId: string) => {
    setShowPostMenu(null);
    navigate(`/feed/edit/${postId}`);
  };

  const handleDeletePost = (post: Post) => {
    setShowPostMenu(null);
    setDeleteConfirmPost(post);
  };

  const confirmDeletePost = async () => {
    if (!deleteConfirmPost) return;

    const postId = deleteConfirmPost.postId;
    setDeleteConfirmPost(null);
    setDeletingPost(postId);

    try {
      const deletePostFunc = httpsCallable(functions, 'deletePost');
      await deletePostFunc({ postId });

      // Remove from local state
      setPosts(prev => prev.filter(p => p.postId !== postId));

      toast.success(t('feed.postDeleted'));
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error(t('feed.deleteError'));
    } finally {
      setDeletingPost(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto mb-4"></div>
          <p className="text-ink-muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-ink">
            💈 {t('feed.title')}
          </h1>

          {/* Settings Button - Only for super_admin */}
          {user?.role === 'super_admin' && (
            <button
              onClick={() => navigate('/admin/post-permissions')}
              className="flex items-center gap-2 px-4 py-2 bg-surface-2 text-ink-muted rounded-lg hover:bg-surface-3 transition"
              title={t('feed.permissionsDescription')}
            >
              <Settings className="w-5 h-5" />
              <span className="hidden sm:inline">{t('feed.permissions')}</span>
            </button>
          )}
        </div>

        {posts.length === 0 ? (
          <div className="bg-surface rounded-lg shadow-sm p-12 text-center">
            <p className="text-ink-muted text-lg mb-2">{t('feed.noPosts')}</p>
            <p className="text-ink-subtle text-ink-muted text-sm">{t('feed.barbersWillPost')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <div key={post.postId} className="bg-surface rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 flex items-center">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {post.authorPhoto ? (
                      <img
                        src={post.authorPhoto}
                        alt={post.authorName}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-info/10 flex items-center justify-center text-info font-bold flex-shrink-0">
                        {post.authorName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-ink truncate">{post.authorName}</p>
                      <p className="text-xs text-ink-subtle truncate">
                        {post.branchName && `${post.branchName} • `}{formatTimeAgo(post.createdAt)}
                      </p>
                    </div>
                  </div>

                  {/* Post Menu Button - Always visible for reporting */}
                  <div className="relative flex-shrink-0 ml-2" ref={showPostMenu === post.postId ? menuRef : null}>
                    <button
                      onClick={() => setShowPostMenu(showPostMenu === post.postId ? null : post.postId)}
                      className="p-2 text-ink-subtle hover:text-ink  hover:bg-surface-2 rounded-full transition"
                      disabled={deletingPost === post.postId}
                    >
                      {deletingPost === post.postId ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-hairline"></div>
                      ) : (
                        <MoreVertical className="w-5 h-5" />
                      )}
                    </button>

                    {/* Dropdown Menu */}
                    {showPostMenu === post.postId && (
                      <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg shadow-lg border border-hairline z-50 overflow-hidden">
                        {canEditPost(post) && (
                          <button
                            onClick={() => handleEditPost(post.postId)}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-ink-muted hover:bg-surface-2 transition"
                          >
                            <Edit2 className="w-4 h-4" />
                            <span>{t('feed.editPost')}</span>
                          </button>
                        )}
                        {canDeletePost(post) && (
                          <button
                            onClick={() => handleDeletePost(post)}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-danger hover:bg-danger/20 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>{t('feed.deletePost')}</span>
                          </button>
                        )}
                        {/* Report Button - Always visible for logged-in users */}
                        {firebaseUser && !isGuest && post.authorId !== firebaseUser.uid && (
                          <button
                            onClick={() => {
                              setShowPostMenu(null);
                              setReportPost(post);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left text-warning hover:bg-warning/20 transition"
                          >
                            <Flag className="w-4 h-4" />
                            <span>{t('report.reportPost')}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Image */}
                <img
                  src={post.imageUrl}
                  alt={post.caption || 'Haircut'}
                  className="w-full aspect-square object-cover"
                />

                {/* Actions */}
                <div className="p-4">
                  <div className="flex items-center gap-4 mb-3">
                    {/* Like button - disabled for guests */}
                    {isGuest ? (
                      <button
                        onClick={() => toast.error(t('feed.createAccount'), { description: t('feed.goToProfile') })}
                        className="opacity-50 cursor-not-allowed"
                        title={t('feed.createAccount')}
                      >
                        <Heart className="w-6 h-6 text-ink-subtle" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleLike(post.postId)}
                        className="transition-transform hover:scale-110"
                      >
                        <Heart
                          className={`w-6 h-6 ${
                            likedPosts.has(post.postId)
                              ? 'fill-danger text-danger'
                              : 'text-ink-muted'
                          }`}
                        />
                      </button>
                    )}
                    <button
                      onClick={() => toggleComments(post.postId)}
                      className="text-ink-muted transition-transform hover:scale-110"
                    >
                      <MessageCircle className="w-6 h-6" />
                    </button>
                    <button className="text-ink-muted transition-transform hover:scale-110">
                      <Send className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Likes count */}
                  {post.likesCount > 0 && (
                    <p className="font-semibold text-ink text-sm mb-2">
                      {post.likesCount} {post.likesCount === 1 ? t('feed.like') : t('feed.likes')}
                    </p>
                  )}

                  {/* Caption */}
                  {post.caption && (
                    <p className="text-ink text-sm mb-2">
                      <span className="font-semibold">{post.authorName}</span>{' '}
                      {post.caption}
                    </p>
                  )}

                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <p className="text-info text-sm mb-2">
                      {post.tags.map(tag => `#${tag}`).join(' ')}
                    </p>
                  )}

                  {/* Tagged Users */}
                  {post.taggedUsers && post.taggedUsers.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-ink-muted mb-2">
                      <span>📷 {t('feed.with')}</span>
                      <div className="flex flex-wrap gap-1">
                        {post.taggedUsers.map((tag, index) => (
                          <span key={tag.userId}>
                            <button className="hover:text-info hover:underline transition">
                              {tag.userName}
                            </button>
                            {index < (post.taggedUsers?.length ?? 0) - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View comments */}
                  {post.commentsCount > 0 && !showComments[post.postId] && (
                    <button
                      onClick={() => toggleComments(post.postId)}
                      className="text-ink-subtle text-sm hover:text-ink "
                    >
                      {t('feed.viewComments', { count: post.commentsCount })}
                    </button>
                  )}

                  {/* Comments */}
                  {showComments[post.postId] && comments[post.postId] && (
                    <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                      {comments[post.postId].map((comment) => (
                        <div key={comment.commentId} className="text-sm group flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-ink mr-2">
                              {comment.userName}
                            </span>
                            <span className="text-ink-muted">{comment.text}</span>
                          </div>
                          {/* Report comment button - visible on hover, not for own comments */}
                          {firebaseUser && comment.userId !== firebaseUser.uid && (
                            <button
                              onClick={() => setReportComment({
                                comment,
                                postId: post.postId,
                                franchiseId: post.franchiseId
                              })}
                              className="opacity-0 group-hover:opacity-100 p-1 text-ink-subtle hover:text-warning transition-opacity flex-shrink-0"
                              title={t('report.reportComment')}
                            >
                              <Flag className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add comment - disabled for guests */}
                  {isGuest ? (
                    <div className="mt-3 flex items-center gap-2 border-t border-hairline pt-3">
                      <p className="text-ink-subtle text-sm italic">
                        {t('feed.createAccountToComment')}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center gap-2 border-t border-hairline pt-3">
                      <input
                        type="text"
                        placeholder={t('feed.addComment')}
                        value={commentText[post.postId] || ''}
                        onChange={(e) => setCommentText(prev => ({ ...prev, [post.postId]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleComment(post.postId);
                          }
                        }}
                        className="flex-1 bg-transparent text-ink text-sm placeholder:text-ink-subtle outline-none"
                      />
                      {commentText[post.postId]?.trim() && (
                        <button
                          onClick={() => handleComment(post.postId)}
                          className="text-info font-semibold text-sm hover:text-info"
                        >
                          {t('feed.publish')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Floating Action Button (only for users with create permission) */}
        {permissions.canCreate && !permissions.loading && (
          <button
            onClick={() => navigate('/feed/create')}
            className="fixed bottom-24 right-6 lg:bottom-8 bg-info text-white p-4 rounded-full shadow-lg hover:bg-info/90 transition-all hover:scale-110 z-50"
          >
            <Plus className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmPost && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="relative p-6 pb-4">
              <button
                onClick={() => setDeleteConfirmPost(null)}
                className="absolute top-4 right-4 text-ink-subtle hover:text-ink  transition"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-danger/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-danger" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-ink">
                    {t('feed.deletePost')}
                  </h3>
                  <p className="text-sm text-ink-subtle">
                    {t('feed.cannotUndo')}
                  </p>
                </div>
              </div>
            </div>

            {/* Post Preview */}
            <div className="px-6 pb-4">
              <div className="flex items-center gap-3 p-3 bg-surface-2 rounded-xl">
                {deleteConfirmPost.imageUrl && (
                  <img
                    src={deleteConfirmPost.imageUrl}
                    alt="Post preview"
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {deleteConfirmPost.authorName}
                  </p>
                  <p className="text-xs text-ink-subtle truncate">
                    {deleteConfirmPost.caption || t('feed.noDescription')}
                  </p>
                  <p className="text-xs text-ink-subtle mt-1">
                    {deleteConfirmPost.likesCount} {t('feed.likes')} • {deleteConfirmPost.commentsCount} {t('feed.comments')}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setDeleteConfirmPost(null)}
                className="flex-1 px-4 py-3 bg-surface-2 text-ink-muted font-medium rounded-xl hover:bg-surface-3 transition"
              >
                {t('feed.cancel')}
              </button>
              <button
                onClick={confirmDeletePost}
                className="flex-1 px-4 py-3 bg-danger text-white font-medium rounded-xl hover:bg-danger/90 transition flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {t('feed.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Post Modal */}
      <ReportContentModal
        isOpen={!!reportPost}
        onClose={() => setReportPost(null)}
        contentType="post"
        contentId={reportPost?.postId || ''}
        contentAuthorId={reportPost?.authorId}
        franchiseId={reportPost?.franchiseId || user?.franchiseId}
      />

      {/* Report Comment Modal */}
      <ReportContentModal
        isOpen={!!reportComment}
        onClose={() => setReportComment(null)}
        contentType="comment"
        contentId={reportComment?.comment.commentId || ''}
        contentAuthorId={reportComment?.comment.userId}
        franchiseId={reportComment?.franchiseId || user?.franchiseId}
      />
    </div>
  );
}
